import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatNativeDateModule } from "@angular/material/core";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIcon } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { catchError, forkJoin, of } from "rxjs";
import {
  DashboardKpi,
  PERIODOS_DASHBOARD,
  GRAPH_COLORS,
  PeriodoDashboard,
  buildDonutStyle,
  fechaATexto,
  formatMinutes,
  obtenerRangoPeriodo,
  ordenarMarcadores,
  porcentaje,
  textoAFecha,
  toNumber,
} from "src/app/dashboard/shared/dashboard-graficas";
import { PestanaDashboard } from "src/app/dashboard/shared/pestana-dashboard";
import { ConsultaList } from "src/app/shared/model/core/ConsultaList.model";
import { tieneAccesoMenu } from "src/app/shared/menu/acceso-menu";
import { Marcadores } from "src/app/shared/model/tickets/Marcadores";
import { Tickets } from "src/app/shared/model/tickets/tickets.model";
import { AuthService } from "src/app/security/auth.service";
import { TicketsService } from "src/app/tickets/tickets.service";
import { TicketsEstatus } from "src/app/utilerias/enums/tickets-estatus.enum";

interface TicketStatusView {
  nombre: string;
  valor: number;
  color: string;
  className: string;
}

interface TiempoEtapa {
  label: string;
  valor: string;
  meta: string;
}

/**
 * Pestaña "Tickets" del Dashboard de Inicio: periodo, KPIs, dona por
 * estatus, tipo de solicitud, areas, tiempos y la tabla reciente (solo
 * para quien tiene TICKETS/Admin). Consulta al montarse; el cascaron
 * (dashini) la vuelve a consultar con actualizar().
 */
@Component({
  selector: "app-dashboard-tickets",
  standalone: true,
  templateUrl: "./dashboard-tickets.component.html",
  styleUrls: ["../../shared/dashboard-tarjetas.css"],
  imports: [
    CommonModule,
    FormsModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIcon,
    MatInputModule,
    MatNativeDateModule,
  ],
})
export class DashboardTicketsComponent implements OnInit, PestanaDashboard {
  readonly periodos: PeriodoDashboard[] = PERIODOS_DASHBOARD;

  periodoSeleccionado = "30";
  fechaInicioInput = "";
  fechaFinInput = "";
  fechaInicioSeleccionada: Date | null = null;
  fechaFinSeleccionada: Date | null = null;
  errorCarga = "";

  kpis: DashboardKpi[] = [];
  estatusItems: TicketStatusView[] = [];
  tipoSolicitudItems: Marcadores[] = [];
  areaItems: Marcadores[] = [];
  tiempos: TiempoEtapa[] = [];
  ticketsRecientes: Tickets[] = [];
  totalTickets = 0;
  canViewTicketsAdmin = false;

  private cargandoDatos = false;
  /** La pestaña se destruye al cambiar de tab: corta la consulta en vuelo. */
  private readonly destroyRef = inject(DestroyRef);
  /** true desde que la pestaña se destruye; guarda el repintado (ver repintar()). */
  private destruido = false;

  private readonly statusColors: Record<string, string> = {
    ABIERTA: "#d32f2f",
    REABIERTA: "#ef6c00",
    PROCESO: "#1565c0",
    ATENDIDA: "#00897b",
    VOBO: "#455a64",
    CANCELADA: "#8d8d8d",
  };

  constructor(
    private ticketsService: TicketsService,
    private authService: AuthService,
    private router: Router,
    // La vista NO se repinta sola cuando llega la respuesta HTTP: en este
    // arranque zone.js no queda parcheando XMLHttpRequest, asi que el
    // estado cambia y el DOM se queda en "Actualizando indicadores..."
    // hasta el siguiente clic. Se pide el repintado a mano, el mismo
    // patron que usa el resto de la casa (asistencia/mi-asistencia).
    // NO quitar por "innecesario".
    private cdr: ChangeDetectorRef,
  ) {
    // Se registra ANTES de cualquier suscripcion: los callbacks de
    // DestroyRef corren en orden de registro, asi que cuando
    // takeUntilDestroyed cierre el stream al destruir la pestaña (y con
    // ello dispare su complete) esto ya vale true.
    this.destroyRef.onDestroy(() => (this.destruido = true));
  }

  ngOnInit() {
    this.canViewTicketsAdmin = tieneAccesoMenu(this.authService.getMenuList(), "TICKETS/Admin");
    this.aplicarPeriodo();
  }

  get cargando(): boolean {
    return this.cargandoDatos;
  }

  actualizar(): void {
    this.consultarDashboard();
  }

  aplicarPeriodo(): void {
    const rango = obtenerRangoPeriodo(this.periodoSeleccionado);
    this.fechaInicioInput = rango.inicio;
    this.fechaFinInput = rango.fin;
    this.fechaInicioSeleccionada = textoAFecha(rango.inicio);
    this.fechaFinSeleccionada = textoAFecha(rango.fin);
    this.consultarDashboard();
  }

  onFechaInicioChange(fecha: Date | null): void {
    this.fechaInicioInput = fechaATexto(fecha);
    this.consultarDashboard();
  }

  onFechaFinChange(fecha: Date | null): void {
    this.fechaFinInput = fechaATexto(fecha);
    this.consultarDashboard();
  }

  consultarDashboard(): void {
    this.cargandoDatos = true;
    this.errorCarga = "";

    const req = this.crearReqMarcadores();

    forkJoin({
      tipos: this.ticketsService.consultaTipoSolicitud(req).pipe(catchError(() => of({ list: [] } as ConsultaList<Marcadores>))),
      areas: this.ticketsService.consultaAreaSolicitante(req).pipe(catchError(() => of({ list: [] } as ConsultaList<Marcadores>))),
      estatus: this.ticketsService.consultaEstatus(req).pipe(catchError(() => of({ list: [] } as ConsultaList<Marcadores>))),
      proceso: this.ticketsService.consultaTiempoProceso(req).pipe(catchError(() => of(null))),
      atendido: this.ticketsService.consultaTiempoAtendido(req).pipe(catchError(() => of(null))),
      vobo: this.ticketsService.consultaTiempoVoBo(req).pipe(catchError(() => of(null))),
      cancelado: this.ticketsService.consultaTiempoCancelado(req).pipe(catchError(() => of(null))),
      abiertos: this.canViewTicketsAdmin ? this.consultarTicketsPorEstatus(TicketsEstatus.ABIERTA) : of({ list: [] } as ConsultaList<Tickets>),
      procesoTickets: this.canViewTicketsAdmin ? this.consultarTicketsPorEstatus(TicketsEstatus.PROCESO) : of({ list: [] } as ConsultaList<Tickets>),
      atendidos: this.canViewTicketsAdmin ? this.consultarTicketsPorEstatus(TicketsEstatus.ATENDIDA) : of({ list: [] } as ConsultaList<Tickets>),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.estatusItems = this.normalizarEstatus(res.estatus?.list || []);
          this.tipoSolicitudItems = ordenarMarcadores(res.tipos?.list || []).slice(0, 6);
          this.areaItems = ordenarMarcadores(res.areas?.list || []).slice(0, 6);
          this.tiempos = this.construirTiempos(res.proceso, res.atendido, res.vobo, res.cancelado);
          this.ticketsRecientes = this.construirTicketsRecientes([
            ...(res.abiertos?.list || []),
            ...(res.procesoTickets?.list || []),
            ...(res.atendidos?.list || []),
          ]);
          this.totalTickets = this.estatusItems.reduce((total, item) => total + item.valor, 0);
          this.kpis = this.construirKpis(res.atendido);
          this.repintar();
        },
        error: (e) => {
          console.error("Error al cargar dashboard de tickets:", e);
          // RxJS no llama a complete despues de un error, asi que aqui hay
          // que apagar la carga: si no, la tarjeta "Actualizando
          // indicadores..." se queda puesta para siempre.
          this.cargandoDatos = false;
          this.errorCarga = "No fue posible cargar la informacion del dashboard.";
          this.repintar();
        },
        complete: () => {
          this.cargandoDatos = false;
          this.repintar();
        },
      });
  }

  /**
   * Repinta la pestaña. detectChanges() sobre una vista ya destruida
   * truena, y el complete SI llega al destruir (takeUntilDestroyed cierra
   * el stream desde el propio onDestroy), de ahi el guardia.
   */
  private repintar(): void {
    if (this.destruido) {
      return;
    }
    // markForCheck ADEMAS de detectChanges: detectChanges refresca solo
    // ESTA vista, y el cascaron (dashini) lee el `cargando` de la pestaña
    // para habilitar su boton "Actualizar". Sin marcar el camino hacia la
    // raiz ese enlace se queda con el valor viejo: el boton se queda
    // deshabilitado para siempre y en desarrollo sale un NG0100.
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  getPeriodoLabel(): string {
    const periodo = this.periodos.find((item) => item.id === this.periodoSeleccionado);
    return periodo?.nombre || "Periodo";
  }

  getBarPercent(item: Marcadores): number {
    return porcentaje(toNumber(item?.valor), this.totalTickets);
  }

  getBarStyle(item: Marcadores, index: number): Record<string, string> {
    return {
      width: `${this.getBarPercent(item)}%`,
      background: GRAPH_COLORS[index % GRAPH_COLORS.length],
    };
  }

  getDonutStyle(): Record<string, string> {
    if (this.totalTickets <= 0 || this.estatusItems.length === 0) {
      return { background: "#eef1f5" };
    }

    return buildDonutStyle(this.estatusItems.map((item) => ({
      color: item.color,
      valor: item.valor,
    })), this.totalTickets);
  }

  getStatusCount(nombre: string): number {
    return this.estatusItems
      .filter((item) => item.nombre === nombre)
      .reduce((total, item) => total + item.valor, 0);
  }

  getTicketEstatusLabel(ticket: Tickets): string {
    switch (Number(ticket?.id_estatus)) {
      case TicketsEstatus.ABIERTA:
        return "ABIERTA";
      case TicketsEstatus.PROCESO:
        return "PROCESO";
      case TicketsEstatus.ATENDIDA:
        return "ATENDIDA";
      case TicketsEstatus.VOBO:
        return "VOBO";
      case TicketsEstatus.CANCELADA:
        return "CANCELADA";
      case TicketsEstatus.REABIERTA:
        return "REABIERTA";
      default:
        return String(ticket?.nombreEstaus || "SIN ESTATUS");
    }
  }

  getTicketEstatusClass(ticket: Tickets): string {
    return `ticket-status--${this.getTicketEstatusLabel(ticket).toLowerCase()}`;
  }

  getPriorityClass(ticket: Tickets): string {
    return `ticket-priority--${String(ticket?.prioridad || "sin").toLowerCase()}`;
  }

  abrirTickets(): void {
    if (!this.canViewTicketsAdmin) {
      return;
    }
    this.router.navigate(["/TICKETS/Admin"]);
  }

  abrirTicket(ticket: Tickets): void {
    if (ticket?.id_ticket) {
      this.router.navigate(["/TICKETS/DetalleTickets", ticket.id_ticket]);
    }
  }

  private crearReqMarcadores(): ConsultaList<Marcadores> {
    const req = new ConsultaList<Marcadores>();
    req.param1 = `${this.fechaInicioInput} 00:00:00`;
    req.param2 = `${this.fechaFinInput} 23:59:59`;
    return req;
  }

  private consultarTicketsPorEstatus(idEstatus: TicketsEstatus) {
    const ticket = new Tickets();
    ticket.id_estatus = idEstatus;
    ticket.id_area = 0;
    ticket.id_ticket = null;
    ticket.asunto = "";
    ticket.fechaSolicitud = null;

    const req = new ConsultaList<Tickets>();
    req.regInicial = 0;
    req.regXPagina = 6;
    req.bean = ticket;

    return this.ticketsService.obtenerTicketsFiltro(req).pipe(
      catchError(() => of({ list: [] } as ConsultaList<Tickets>))
    );
  }

  private construirKpis(tiempoAtendido: Marcadores | null): DashboardKpi[] {
    const abiertas = this.getStatusCount("ABIERTA") + this.getStatusCount("REABIERTA");
    const proceso = this.getStatusCount("PROCESO");
    const atendidas = this.getStatusCount("ATENDIDA") + this.getStatusCount("VOBO");
    const tiempo = formatMinutes(toNumber(tiempoAtendido?.valor));

    return [
      {
        label: "Tickets generados",
        value: this.totalTickets,
        meta: this.getPeriodoLabel(),
        icon: "confirmation_number",
        className: "ticket-kpi--total",
      },
      {
        label: "Pendientes activos",
        value: abiertas + proceso,
        meta: `${abiertas} abiertos / ${proceso} en proceso`,
        icon: "pending_actions",
        className: "ticket-kpi--active",
      },
      {
        label: "Atendidos",
        value: atendidas,
        meta: "Atendida + VoBo",
        icon: "task_alt",
        className: "ticket-kpi--done",
      },
      {
        label: "Promedio a atencion",
        value: tiempo,
        meta: "Desde solicitud hasta atencion",
        icon: "timer",
        className: "ticket-kpi--time",
      },
    ];
  }

  private construirTiempos(
    proceso: Marcadores | null,
    atendido: Marcadores | null,
    vobo: Marcadores | null,
    cancelado: Marcadores | null
  ): TiempoEtapa[] {
    return [
      { label: "Proceso", valor: formatMinutes(toNumber(proceso?.valor)), meta: "Solicitud a proceso" },
      { label: "Atendido", valor: formatMinutes(toNumber(atendido?.valor)), meta: "Solicitud a atencion" },
      { label: "VoBo", valor: formatMinutes(toNumber(vobo?.valor)), meta: "Solicitud a visto bueno" },
      { label: "Cancelado", valor: formatMinutes(toNumber(cancelado?.valor)), meta: "Solicitud a cancelacion" },
    ];
  }

  private normalizarEstatus(items: Marcadores[]): TicketStatusView[] {
    return ordenarMarcadores(items).map((item) => {
      const nombre = String(item.nombre || "SIN ESTATUS").toUpperCase();
      return {
        nombre,
        valor: toNumber(item.valor),
        color: this.statusColors[nombre] || "#607d8b",
        className: `legend-dot--${nombre.toLowerCase()}`,
      };
    });
  }

  private construirTicketsRecientes(tickets: Tickets[]): Tickets[] {
    const unique = new Map<number, Tickets>();
    tickets.forEach((ticket) => {
      if (ticket?.id_ticket) {
        unique.set(ticket.id_ticket, ticket);
      }
    });

    return [...unique.values()]
      .sort((a, b) => new Date(b.fechaSolicitud || 0).getTime() - new Date(a.fechaSolicitud || 0).getTime())
      .slice(0, 6);
  }
}
